import React from 'react'
import { Button } from 'antd'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import { trackEvent } from '../../utils/analytics'

interface ColumnMapping {
  title: string
  dataIndex: string
}

interface AdditionalSheet {
  name: string
  data: any[]
  columnMapping?: ColumnMapping[]
}

interface ExcelExporterProps {
  data: any[]
  visibleColumns?: string[]
  columnMapping?: ColumnMapping[]
  summaryRow?: Record<string, any>
  excludeFields?: string[]
  filename?: string
  buttonText?: string
  className?: string
  trackingContext?: Record<string, any> // Optional context for tracking
  onBeforeExport?: () => Promise<AdditionalSheet[]>
  exportingText?: string
}

const ExcelExporter: React.FC<ExcelExporterProps> = ({
  data,
  visibleColumns,
  columnMapping,
  summaryRow,
  excludeFields = [],
  filename = 'Export',
  buttonText = 'Export to Excel',
  className,
  trackingContext = {},
  onBeforeExport,
  exportingText = 'Preparing export...'
}) => {
  const [isExporting, setIsExporting] = React.useState(false)

  const handleExport = async () => {
    setIsExporting(true)
    try {
      await performExport()
    } finally {
      setIsExporting(false)
    }
  }

  const performExport = async () => {
    const exportData = summaryRow ? [...data, summaryRow] : [...data]

    if (exportData.length === 0) {
      console.warn('No data to export')
      trackEvent('report_data_exported', {
        ...trackingContext,
        success: false,
        errorReason: 'no_data',
      })
      return
    }

    // Standard fields to always exclude from export
    const defaultExcludeFields = [
      'key',
      'trafficDriverId',
      'publicationId',
      'websiteId',
      'type',
      'removed',
      'total_readtime',
      'children' // Don't export nested data structures
    ]

    const allExcludeFields = [...defaultExcludeFields, ...excludeFields]

    // Build mapping from dataIndex to human-readable titles
    const titleMap: Record<string, string> = {}
    if (columnMapping) {
      columnMapping.forEach(col => {
        titleMap[col.dataIndex] = col.title
      })
    }

    // Filter data to only include visible columns and exclude sensitive fields
    const filteredData = exportData.map(row => {
      const filteredRow: any = {}

      // If visibleColumns is specified, only include those columns
      const columnsToCheck = visibleColumns || Object.keys(row)

      columnsToCheck.forEach(column => {
        // Skip if column should be excluded or doesn't exist in row
        if (allExcludeFields.includes(column) || row[column] === undefined) {
          return
        }

        // Use human-readable title if available, otherwise use the column name
        const columnTitle = titleMap[column] || column

        // Convert React elements to strings for Excel export
        if (React.isValidElement(row[column])) {
          filteredRow[columnTitle] = extractTextFromReactElement(row[column])
        } else {
          filteredRow[columnTitle] = row[column]
        }
      })

      return filteredRow
    })

    // Fetch additional sheets if needed
    let additionalSheets: AdditionalSheet[] = []
    if (onBeforeExport) {
      additionalSheets = await onBeforeExport()
    }

    // Create workbook and add main sheet
    const workbook = XLSX.utils.book_new()
    const worksheet = XLSX.utils.json_to_sheet(filteredData)

    // Auto-size columns for main sheet
    autoSizeColumns(worksheet)

    // Add main worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data')

    // Add additional sheets
    for (const sheet of additionalSheets) {
      const additionalWorksheet = XLSX.utils.json_to_sheet(sheet.data)
      autoSizeColumns(additionalWorksheet)
      XLSX.utils.book_append_sheet(workbook, additionalWorksheet, sheet.name)
    }

    // Generate Excel file and trigger download
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array'
    })

    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
    })

    saveAs(blob, `${filename}.xlsx`)

    // Track successful export
    trackEvent('report_data_exported', {
      ...trackingContext,
      success: true,
      filename,
      rowCount: exportData.length,
      columnCount: visibleColumns?.length || Object.keys(exportData[0] || {}).length,
      hasSummaryRow: !!summaryRow,
    })
  }

  // Helper function to auto-size columns
  const autoSizeColumns = (worksheet: any) => {
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1')
    const colWidths: any[] = []

    for (let C = range.s.c; C <= range.e.c; ++C) {
      let maxWidth = 10
      for (let R = range.s.r; R <= range.e.r; ++R) {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C })
        const cell = worksheet[cellRef]
        if (cell && cell.v) {
          const cellLength = cell.v.toString().length
          if (cellLength > maxWidth) {
            maxWidth = Math.min(cellLength, 50) // Cap at 50 characters
          }
        }
      }
      colWidths.push({ width: maxWidth })
    }
    worksheet['!cols'] = colWidths
  }

  // Helper function to extract text from React elements
  const extractTextFromReactElement = (element: any): string => {
    if (typeof element === 'string' || typeof element === 'number') {
      return element.toString()
    }

    if (React.isValidElement(element)) {
      // Handle common cases where we have wrapped values
      const props = element.props as any
      if (props && props.children) {
        const children = props.children
        if (Array.isArray(children)) {
          return children
            .map(child => extractTextFromReactElement(child))
            .join('')
        } else {
          return extractTextFromReactElement(children)
        }
      }
    }

    // Fallback to string conversion
    return element?.toString() || ''
  }

  return (
    <Button
      onClick={handleExport}
      className={className}
      loading={isExporting}
    >
      {isExporting ? exportingText : buttonText}
    </Button>
  )
}

export default ExcelExporter